<script setup lang="ts">
import { computed, type Ref } from 'vue';
import { toPlainText } from '@portabletext/toolkit';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import type { FAQ } from '@pkg/sanity-utils/types/faq';
import {
  Accordion as UiAccordion,
  AccordionContent as UiAccordionContent,
  AccordionItem as UiAccordionItem,
  AccordionTrigger as UiAccordionTrigger,
} from '@pkg/ui/components/ui/accordion';

interface Props {
  items?: FAQ['items'];
}

const activeFaq: Ref<string> = useState<string>('activeFaq');

const props = defineProps<Props>();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();

const baseUrl = computed(() => {
  const domain = String(runtimeConfig.public.domain || '').trim();
  if (!domain) {
    return '';
  }
  return domain.startsWith('http') ? domain : `https://${domain}`;
});

function handleActiveFaq(faqKey: string) {
  activeFaq.value = faqKey;
}

const faqPageJsonLd = computed(() => {
  const questions = (props.items || [])
    .map((item) => {
      const name = item.question?.trim();
      if (!name) {
        return null;
      }

      const text = typeof item.answer === 'string'
        ? item.answer.trim()
        : item.answer?.length
          ? toPlainText(item.answer).trim()
          : '';

      if (!text) {
        return null;
      }

      return {
        '@type': 'Question',
        name,
        acceptedAnswer: {
          '@type': 'Answer',
          text,
        },
      };
    })
    .filter(Boolean);

  if (!questions.length) {
    return null;
  }

  const path = route.path.replace(/\/$/, '') || '/';
  const pageUrl = `${baseUrl.value}${path.startsWith('/') ? path : `/${path}`}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faq`,
    mainEntity: questions,
  };
});

useHead({
  script: computed(() => {
    const schema = faqPageJsonLd.value;
    if (!schema) {
      return [];
    }

    // Stable key so multiple FAQ blocks on one page share a single FAQPage @id.
    return [
      {
        key: 'faq-jsonld',
        type: 'application/ld+json',
        innerHTML: JSON.stringify(schema),
      },
    ];
  }),
});
</script>

<template>
  <UiAccordion v-if="items" type="single" collapsible @update:model-value="key => handleActiveFaq(key as string)">
    <UiAccordionItem v-for="item in items" :id="item._key" :key="item._key" :value="item._key">
      <UiAccordionTrigger>
        {{ item.question }}
      </UiAccordionTrigger>
      <UiAccordionContent v-if="item.answer">
        <p v-if="typeof item.answer === 'string'">
          {{ item.answer }}
        </p>
        <PortableEditor v-else :content="item.answer" />
      </UiAccordionContent>
    </UiAccordionItem>
  </UiAccordion>
</template>
